import * as React from "react";

import type { TabListProps } from "./TabList.types";
import { ArrowLeft, ArrowRight } from "@fluentui/keyboard-keys";
import { splitTabHeadersIntoOverflow } from "../splitTabHeadersIntoOverflow";
import { useDir } from "@1js/acui-common";

export const useOnKeyDown: (
  props: TabListProps,
  currentSelectedId: string,
  setSelectedId: React.Dispatch<React.SetStateAction<string>>,
  setFocusOnOverflow: React.Dispatch<React.SetStateAction<boolean>>,
  onTabSelect: (
    tabID: string,
    ev?:
      | React.MouseEvent<HTMLDivElement, MouseEvent>
      | React.KeyboardEvent<HTMLDivElement>
  ) => void,
  focusOnOverflow: boolean
) => React.KeyboardEventHandler<HTMLDivElement> = (
  props,
  currentSelectedId,
  setSelectedId,
  setFocusOnOverflow,
  onTabSelect,
  focusOnOverflow
) => {
  const {
    onKeyDown,
    topLevelTabHeaders,
    overflowTabHeaders,
    getDroppedTabCount,
  } = props;

  const { isRTL } = useDir();
  const isDirRTL = isRTL();
  return React.useCallback(
    (event) => {
      let currentTopLevelTabHeaders = topLevelTabHeaders;
      let currentOverflowTabHeaders = overflowTabHeaders;
      // if getDroppedTabCount is present, use it to determine the set of visible top level items
      const tabsDroppedWithResponsiveScaling = getDroppedTabCount?.();
      if (tabsDroppedWithResponsiveScaling !== undefined) {
        const splitTabs = splitTabHeadersIntoOverflow(
          topLevelTabHeaders,
          tabsDroppedWithResponsiveScaling,
          currentSelectedId
        );
        currentTopLevelTabHeaders = splitTabs.topLevelTabHeaders;
        currentOverflowTabHeaders = splitTabs.overflowTabHeaders;
      }

      const hasOverflow =
        currentOverflowTabHeaders && currentOverflowTabHeaders.length > 0;
      // create an array of the tabs to navigate
      const tabsArray = Array.from(currentTopLevelTabHeaders).map(
        (header) => header.id
      );
      const tabsArrayLength = tabsArray.length;

      // Find the current index, needed incase of resize
      let currentIndex = focusOnOverflow ? tabsArrayLength : 0;
      if (!focusOnOverflow) {
        currentIndex = tabsArray.findIndex(
          (element: string) => element === currentSelectedId
        );
      }
      if (currentIndex < 0) {
        currentIndex = 0;
      }

      // see if we are increasing across the tabs
      if (
        !event.ctrlKey &&
        ((!isDirRTL && event.key === ArrowRight) ||
          (isDirRTL && event.key === ArrowLeft))
      ) {
        const localIndex = currentIndex + 1;
        // First account for the case where focus on the overflow
        // and we are moving off of it. Or if we are at end of tabs.
        // We need to ensure we set focusOnOverflow back to false
        if (
          focusOnOverflow ||
          (localIndex === tabsArrayLength && !hasOverflow)
        ) {
          const id = tabsArray[0];
          // fire the tab select event to select the new tab
          onTabSelect(id, event);
          // update the selected ID and current index in the state
          setSelectedId(id);
          setFocusOnOverflow(false);
        }
        // if we have overflow and are at end of tabs, focus on on overflow update index only
        else if (localIndex === tabsArrayLength && hasOverflow) {
          // do nothing, focus is on the overflow
          setFocusOnOverflow(true);
        }
        // lastly if we are just navigating tabs, switch to the next one
        else {
          const id = tabsArray[localIndex];
          // fire the tab select event to select the new tab
          onTabSelect(id, event);
          // update the selected ID and current index in the state
          setSelectedId(id);
        }
      }
      // see if we are decreasing across the tabs
      else if (
        !event.ctrlKey &&
        ((!isDirRTL && event.key === ArrowLeft) ||
          (isDirRTL && event.key === ArrowRight))
      ) {
        const localIndex = currentIndex - 1;
        // First account for the case where focus on the overflow
        // and we are moving off of it. We need to ensure we set
        // focusOnOverflow back to false. OR check if we are looping
        // back from the first tab to the last tab when there is no overflow
        if (focusOnOverflow || (localIndex < 0 && !hasOverflow)) {
          const id = tabsArray[tabsArrayLength - 1];
          // fire the tab select event to select the new tab
          onTabSelect(id, event);
          // update the selected ID and current index in the state
          setSelectedId(id);
          setFocusOnOverflow(false);
        }
        // check if we are looping back from the
        // first tab to the overflow, tab selection remains
        // on the first tab but focus moves
        else if (localIndex < 0 && hasOverflow) {
          // do nothing, focus is on the overflow
          setFocusOnOverflow(true);
        }
        // lastly if we are just navigating tabs, switch to the next one
        else {
          const id = tabsArray[localIndex];
          // fire the tab select event to select the new tab
          onTabSelect(id, event);
          // update the selected ID and current index in the state
          setSelectedId(id);
          setFocusOnOverflow(false);
        }
      }
      // bubble up the onkeydownevent if needed
      if (onKeyDown) {
        onKeyDown(event);
      }
    },
    [
      onKeyDown,
      setFocusOnOverflow,
      topLevelTabHeaders,
      onTabSelect,
      overflowTabHeaders,
      getDroppedTabCount,
      focusOnOverflow,
      currentSelectedId,
      setSelectedId,
      isDirRTL,
    ]
  );
};
